import { i18n } from '@lingui/core'
import { t } from '@lingui/macro'
import { StyleSheet, Text, View } from '@react-pdf/renderer'
import { currencyFormatedString } from 'app/views/common/Formats'
import { PdfSplittableTableRow } from 'app/views/forms/pdf-components/PdfSplittableTableRow'
import { PdfTableCell } from 'app/views/forms/pdf-components/PdfTableCell'
import { PdfTableHeaderCell } from 'app/views/forms/pdf-components/PdfTableHeaderCell'
import {
  pdfTableBoldTextStyle,
  pdfTableHelpTextStyle,
  pdfTableStyles
} from 'app/views/forms/pdf-components/PdfTableStyles'
import { PdfUnsplittableTableRow } from 'app/views/forms/pdf-components/PdfUnsplittableTableRow'
import React from 'react'
import { sanitizeLineKey } from './FormBudget'

const styles = StyleSheet.create({
  firstCol: {
    width: '20%'
  },
  secondCol: {
    width: '15%'
  },
  thirdCol: {
    width: '65%'
  },
  fullWidthCol: {
    width: '100%'
  }
})

interface BudgetLineValue {
  value: number
  comment?: string
}

interface BudgetValue {
  expenses: Record<string, BudgetLineValue>
  revenues: Record<string, BudgetLineValue>
}

interface BudgetLineSummary {
  value: number
  comments: string[]
}

interface FormBudgetPdfTypeProps {
  expensesLines?: string[]
  revenuesLines?: string[]
  expensesHelpText?: Record<string, string>
  revenuesHelpText?: Record<string, string>
}

interface FormBudgetPdfProps {
  value: BudgetValue
  typeProps: FormBudgetPdfTypeProps
  langVersion: string
}

const summarizeLines = (
  lines: Record<string, BudgetLineValue>
): Record<string, BudgetLineSummary> => {
  const summary: Record<string, BudgetLineSummary> = {}
  Object.entries(lines).forEach(([key, line]) => {
    const existing = summary[key] || { value: 0, comments: [] }
    existing.value += line.value
    if (line.comment) {
      existing.comments.push(line.comment + '\n')
    }
    summary[key] = existing
  })
  return summary
}

export const FormBudgetPdf = ({
  value,
  typeProps,
  langVersion
}: FormBudgetPdfProps): React.ReactElement => {
  const expenses = typeProps.expensesLines || []
  const revenues = typeProps.revenuesLines || []
  const expensesHelpText = typeProps.expensesHelpText?.[langVersion] || ''
  const revenuesHelpText = typeProps.revenuesHelpText?.[langVersion] || ''

  const expensesSummary = summarizeLines(value.expenses)
  const revenuesSummary = summarizeLines(value.revenues)

  const renderLine = (
    label: string,
    line: BudgetLineSummary | undefined,
    key: number
  ): React.ReactElement => (
    <PdfSplittableTableRow key={key}>
      <PdfTableCell style={styles.firstCol}>
        <Text style={pdfTableStyles.secondLevelRowCell}>{i18n._(label)}</Text>
      </PdfTableCell>
      <PdfTableCell style={styles.secondCol}>
        <Text>{currencyFormatedString(line?.value, langVersion)}</Text>
      </PdfTableCell>
      <PdfTableCell style={styles.thirdCol}>
        <Text style={{ display: 'flex', flexDirection: 'column' }}>
          {line?.comments.map((comment, index) => (
            <Text style={{ width: '100%' }} key={index}>
              {comment}
            </Text>
          ))}
        </Text>
      </PdfTableCell>
    </PdfSplittableTableRow>
  )

  return (
    <View style={pdfTableStyles.table}>
      <PdfUnsplittableTableRow>
        <PdfTableHeaderCell style={styles.firstCol} />
        <PdfTableHeaderCell style={styles.secondCol}>{i18n._(t`Amount`)}</PdfTableHeaderCell>
        <PdfTableHeaderCell style={styles.thirdCol}>{i18n._(t`Comments`)}</PdfTableHeaderCell>
      </PdfUnsplittableTableRow>

      <PdfUnsplittableTableRow>
        <PdfTableCell style={styles.fullWidthCol}>
          <Text style={pdfTableBoldTextStyle}>{i18n._(t`Revenues`)}</Text>
          {revenuesHelpText ? <Text style={pdfTableHelpTextStyle}>{revenuesHelpText}</Text> : null}
        </PdfTableCell>
      </PdfUnsplittableTableRow>
      {revenues.map((item, index) => renderLine(item, revenuesSummary[item], index))}
      <PdfUnsplittableTableRow>
        <PdfTableCell style={styles.fullWidthCol}>
          <Text style={pdfTableBoldTextStyle}>{i18n._(t`Expenses`)}</Text>
          {expensesHelpText ? <Text style={pdfTableHelpTextStyle}>{expensesHelpText}</Text> : null}
        </PdfTableCell>
      </PdfUnsplittableTableRow>
      {expenses.map((item, index) =>
        renderLine(item, expensesSummary[sanitizeLineKey(item)], index)
      )}
    </View>
  )
}
